/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Seo, Footer, KoinosWordmark, KedemWordmark } from '@components/systems'
import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'
import { productLineList, ProductLineType } from '@src/configs/products.config'
import { buildItemListJsonLd } from '@utils'

import { useNavigate, useLocation } from 'react-router-dom'

import pls8Img from '@public/images/monolith/koinos8-angle-full.png'
import kedem6Img from '@public/images/pages/shop/kedem6/thumbnail/kedem6-thumb2.jpg'
import kedem4Img from '@public/images/pages/shop/kedem4/thumbnail/kedem4-thumb2.jpg'
import amp2Img from '@public/images/pages/shop/amp2/thumbnail/amp2-thumb2.jpg'
import dsImg from '@public/images/pages/shop/dualSwitch/thumbnail/ds-thumb2.jpg'

/** 코드 → 카드 이미지. Cart·Manuals 와 같은 썸네일 컷. 제품이 늘면 여기에 한 줄. */
const imageObj: { [code: string]: string } = {
	pls8: pls8Img,
	kdm6: kedem6Img,
	kdm4: kedem4Img,
	amp2: amp2Img,
	ds: dsImg,
}

/**
 * Products — 라인업 인덱스 (2026-09-09, SITEMAP_PLAN §8.3·§9).
 *
 * Shop(구매) 과 분리된 "보는" 페이지. 가격·장바구니·시즌 칩이 없다.
 * 라인 행 3개(Koinos · Utility · Kedem) — Support 허브와 같은 4:8 헤어라인 문법.
 * 순서의 SSOT 는 `products.config.ts` 의 배열이다. 단종된 Kedem 이 맨 아래에 서고,
 * 그 행 헤드에는 상태 줄(단정문 세 문장)이 붙는다 (2026-09-16).
 * 좌 = 라인 워드마크 + 키커(풀네임 · 코드), 우 = 그 라인의 제품 카드 → /products/{code}.
 * 라인 페이지는 두지 않는다. 드로어의 Koinos · Kedem · Utility 는 이 행의 앵커로 온다.
 */
const MonolithProductsPage = () => {
	// hook
	const navigate = useNavigate()
	const location = useLocation()

	const jsonLd = React.useMemo(
		() =>
			buildItemListJsonLd(
				productLineList.flatMap(aLine =>
					aLine.products.map(aProduct => ({
						name: aProduct.name,
						path: `/products/${aProduct.code}`,
					})),
				),
			),
		[],
	)

	// `/products#kedem` — 라우터는 해시를 스크롤하지 않고 route.saga 가 맨 위로 올린다. 그 뒤에 직접 내린다.
	React.useEffect(() => {
		if (location.hash === '') return
		const el = document.getElementById(location.hash.slice(1))
		if (el !== null) {
			window.setTimeout(() => el.scrollIntoView({ block: 'start' }), 0)
		}
	}, [location.hash])

	const go = (path: string) => (e: React.MouseEvent) => {
		e.preventDefault()
		navigate(path)
	}

	const renderLineTitle = (aLine: ProductLineType) => {
		if (aLine.id === 'koinos') {
			return <KoinosWordmark as="span" brandOnly={true} />
		}
		if (aLine.id === 'kedem') {
			return <KedemWordmark as="span" />
		}
		// Utility 는 라인이 아니라 카테고리 — 워드마크 없음, 본문 서체
		return <span className="mnLineName">{aLine.name}</span>
	}

	return (
		<View css={style}>
			<Seo jsonLd={jsonLd} />

			<View className="mnWrap">
				<View className="mnPageHeader">
					<h1 className="mnPageTitle">Products</h1>
					<p className="mnPageLede">Two lines and the hardware around them.</p>
				</View>

				<View className="mnLines">
					{productLineList.map(aLine => (
						<View key={aLine.id} id={aLine.id} className="mnLineRow">
							<View className="mnLineHead">
								<h2 className="mnLineTitle">{renderLineTitle(aLine)}</h2>
								{aLine.fullName !== undefined && (
									<span className="mnKicker mnLineKicker">
										{aLine.fullName}
										{aLine.lineCode !== undefined && (
											<span className="mnLineCode"> · {aLine.lineCode}</span>
										)}
									</span>
								)}
								{/* 단종 상태 — 사실만 (PERSONA §1). 소유자에게 남는 것을 마지막 줄에 둔다 */}
								{aLine.status !== undefined && (
									<View className="mnLineStatus">
										<span className="mnKicker mnLineStatusLabel">
											{aLine.status.label}
										</span>
										<p className="mnLineStatusLine">{aLine.status.line}</p>
										<p className="mnLineStatusNote">{aLine.status.note}</p>
									</View>
								)}
							</View>

							<View className="mnLineBody">
								{aLine.products.map(aProduct => {
									const path = `/products/${aProduct.code}`
									const image = imageObj[aProduct.code]
									return (
										<a
											key={aProduct.code}
											className="mnCard"
											href={path}
											onClick={go(path)}
										>
											{image !== undefined && (
												<img
													src={image}
													alt={aProduct.name}
													className={`mnCardImage ${
														aProduct.code === 'pls8'
															? 'mnCardImageProduct'
															: 'mnCardImagePhoto'
													}`}
												/>
											)}
											<View className="mnCardText">
												{aProduct.discontinued === true && (
													<span className="mnCardTag">Discontinued</span>
												)}
												<h3 className="mnCardTitle">
													{aProduct.code === 'pls8' ? (
														<KoinosWordmark as="span" />
													) : aProduct.code.startsWith('kdm') ? (
														<KedemWordmark
															as="span"
															model={aProduct.code.replace('kdm', '')}
														/>
													) : (
														aProduct.name
													)}
												</h3>
												<p className="mnCardBody">{aProduct.summary}</p>
											</View>
											<span className="mnTextLink mnCardLink">View product</span>
										</a>
									)
								})}
							</View>
						</View>
					))}
				</View>

				<View className="mnShopLine">
					<a className="mnTextLink" href="/shop" onClick={go('/shop')}>
						Pricing and availability in the shop
					</a>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithProductsPage

const style = css({
	...monoShared,
	position: 'relative',
	width: '100%',

	// 페이지 헤더 — 1열. 제목은 전폭, 소제목은 그 아래(DESIGN_SYSTEM §6.2). 4:8 은 두 단어 제목(Works with)이 줄바꿈돼 폐기.
	'.mnPageHeader': {
		padding: 'clamp(64px, 8.3vw, 120px) 0 clamp(48px, 6.25vw, 90px)',

		'.mnPageTitle': {
			fontSize: 'clamp(56px, 7.6vw, 110px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},
		'.mnPageLede': {
			fontSize: 19,
			lineHeight: 1.6,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 520,
			marginTop: 'clamp(20px, 2vw, 28px)',
		},
	},

	'.mnLines': {
		borderTop: `1px solid ${mono.line}`,
	},

	'.mnLineRow': {
		// 고정 네비(72) 아래로 앵커가 착지하게
		scrollMarginTop: 72,
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 4fr) minmax(0, 8fr)',
		gap: 48,
		padding: 'clamp(48px, 5vw, 72px) 0',
		borderBottom: `1px solid ${mono.line}`,
		alignItems: 'start',

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 28,
		},
	},

	'.mnLineHead': {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		position: 'sticky',
		top: 72 + 40,

		'.mnLineTitle': {
			fontSize: 'clamp(32px, 3.2vw, 44px)',
			lineHeight: 1,
			fontWeight: 300,
			color: mono.ink,
		},
		'.mnLineName': {
			fontWeight: 300,
		},
		'.mnLineKicker': {
			whiteSpace: 'nowrap',
		},
		'.mnLineCode': {
			color: mono.dim,
		},

		// 단종 줄 — 키커 아래 헤어라인 하나로 끊고 시작한다
		'.mnLineStatus': {
			display: 'flex',
			flexDirection: 'column',
			gap: 8,
			marginTop: 4,
			paddingTop: 20,
			borderTop: `1px solid ${mono.line}`,
			maxWidth: 340,

			'.mnLineStatusLabel': {
				color: mono.ink,
			},
			'.mnLineStatusLine': {
				fontSize: 15,
				lineHeight: 1.65,
				fontWeight: 300,
				color: mono.ink,
			},
			'.mnLineStatusNote': {
				fontSize: 14,
				lineHeight: 1.6,
				fontWeight: 300,
				color: mono.dim,
			},
		},

		'@media (max-width: 900px)': {
			position: 'static',
		},
	},

	'.mnLineBody': {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: 32,

		'@media (max-width: 700px)': {
			gridTemplateColumns: '1fr',
		},
	},

	'.mnCard': {
		display: 'flex',
		flexDirection: 'column',
		gap: 22,
		background: mono.surface,
		border: `1px solid ${mono.line}`,
		padding: 'clamp(22px, 2.4vw, 32px)',
		color: mono.ink,
		transition: 'border-color 200ms ease-out',

		'&:hover': {
			borderColor: mono.lineSoft,
		},
		'&:hover .mnCardLink': {
			borderBottomColor: mono.ink,
		},
		'&:focus-visible': {
			outline: `1px solid ${mono.ink}`,
			outlineOffset: -1,
		},

		'.mnCardImage': {
			width: '100%',
			display: 'block',
			aspectRatio: '1.45 / 1',
			objectFit: 'cover',
		},
		'.mnCardImageProduct': {
			objectFit: 'contain',
			background: mono.surface,
		},
		'.mnCardImagePhoto': {
			border: `1px solid ${mono.line}`,
			borderRadius: 6,
			background: mono.surface,
		},

		'.mnCardText': {
			display: 'flex',
			flexDirection: 'column',
			gap: 10,
			alignItems: 'flex-start',
		},
		'.mnCardTag': {
			fontSize: 11,
			letterSpacing: '.18em',
			textTransform: 'uppercase',
			fontWeight: 500,
			color: mono.faint,
			border: `1px solid ${mono.line}`,
			borderRadius: 2,
			padding: '4px 8px',
			marginBottom: 2,
		},
		'.mnCardTitle': {
			fontSize: 28,
			fontWeight: 400,
			lineHeight: 1.1,
			color: mono.ink,
		},
		'.mnCardBody': {
			fontSize: 15,
			lineHeight: 1.65,
			fontWeight: 300,
			color: mono.dim,
		},
		'.mnCardLink': {
			alignSelf: 'flex-start',
			borderBottomColor: 'transparent',
			transition: 'border-color 200ms ease-out',
		},
	},

	'.mnShopLine': {
		padding: 'clamp(36px, 3.6vw, 52px) 0 clamp(64px, 8.3vw, 120px)',
	},
})
